/**
 * Task Checklist Service
 *
 * Handles task-level checklist items (sub-items within a task).
 * Uses the `task_checklist_items` table (migration 008).
 *
 * @module services/taskChecklistService
 */

import { supabase } from './supabase';

/**
 * Get all checklist items for a task
 * @param {string} taskId - Task ID
 * @returns {Promise<{data, error}>}
 */
export const getTaskChecklistItems = async (taskId) => {
  try {
    const { data, error } = await supabase
      .from('task_checklist_items')
      .select(`
        *,
        completed_by_profile:profiles!completed_by(id, full_name)
      `)
      .eq('task_id', taskId)
      .order('sort_order', { ascending: true });

    if (error) throw error;

    return { data: data || [], error: null };
  } catch (error) {
    console.error('[TASK_CHECKLIST] Get items error:', error);
    return { data: [], error };
  }
};

/**
 * Add a checklist item to a task
 * @param {string} taskId - Task ID
 * @param {string} description - Item description
 * @returns {Promise<{data, error}>}
 */
export const addTaskChecklistItem = async (taskId, description) => {
  try {
    // Get max sort_order
    const { data: existing } = await supabase
      .from('task_checklist_items')
      .select('sort_order')
      .eq('task_id', taskId)
      .order('sort_order', { ascending: false })
      .limit(1);

    const nextOrder = existing?.length > 0 ? existing[0].sort_order + 1 : 0;

    const { data, error } = await supabase
      .from('task_checklist_items')
      .insert({
        task_id: taskId,
        description,
        sort_order: nextOrder,
        is_completed: false,
      })
      .select(`
        *,
        completed_by_profile:profiles!completed_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[TASK_CHECKLIST] Add item error:', error);
    return { data: null, error };
  }
};

/**
 * Toggle a checklist item's completion status
 * @param {string} itemId - Item ID
 * @param {boolean} completed - New completion state
 * @returns {Promise<{data, error}>}
 */
export const toggleTaskChecklistItem = async (itemId, completed) => {
  try {
    const updateData = completed
      ? {
          is_completed: true,
          completed_at: new Date().toISOString(),
        }
      : {
          is_completed: false,
          completed_by: null,
          completed_at: null,
        };

    // If completing, set completed_by to current user
    if (completed) {
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      if (userError) throw userError;
      updateData.completed_by = user.id;
    }

    const { data, error } = await supabase
      .from('task_checklist_items')
      .update(updateData)
      .eq('id', itemId)
      .select(`
        *,
        completed_by_profile:profiles!completed_by(id, full_name)
      `)
      .single();

    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('[TASK_CHECKLIST] Toggle item error:', error);
    return { data: null, error };
  }
};

/**
 * Delete a checklist item
 * @param {string} itemId - Item ID
 * @returns {Promise<{success, error}>}
 */
export const deleteTaskChecklistItem = async (itemId) => {
  try {
    const { error } = await supabase
      .from('task_checklist_items')
      .delete()
      .eq('id', itemId);

    if (error) throw error;

    return { success: true, error: null };
  } catch (error) {
    console.error('[TASK_CHECKLIST] Delete item error:', error);
    return { success: false, error };
  }
};
